import { Pause, Play, SkipBack, SkipForward, Volume2 } from "lucide-react";

const fmt = (s) => {
    if (!s || Number.isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export default function AudioPlayer({
    track,
    isPlaying,
    time,
    duration,
    volume,
    onToggle,
    onNext,
    onPrev,
    onSeek,
    onVolume,
}) {
    const pct = duration ? (time / duration) * 100 : 0;

    return (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
            <div
                data-testid="audio-player"
                className="pointer-events-auto flex w-full max-w-2xl items-center gap-4 rounded-full border border-white/10 bg-black/60 px-5 py-3 shadow-2xl shadow-black/60 backdrop-blur-xl md:gap-6 md:px-7"
            >
                {track.artwork && (
                    <img
                        data-testid="player-artwork"
                        src={track.artwork}
                        alt={`${track.title} artwork`}
                        className="h-10 w-10 rounded-full object-cover"
                    />
                )}
                <div className="flex items-center gap-2">
                    <button
                        data-testid="prev-track-button"
                        onClick={onPrev}
                        aria-label="Previous track"
                        className="text-zinc-400 transition-all duration-300 hover:text-bone active:scale-90"
                    >
                        <SkipBack className="h-4 w-4" />
                    </button>
                    <button
                        data-testid="play-pause-button"
                        onClick={onToggle}
                        aria-label={isPlaying ? "Pause" : "Play"}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-bone text-void transition-all duration-300 hover:bg-blood hover:text-bone active:scale-90"
                    >
                        {isPlaying ? (
                            <Pause className="h-4 w-4" />
                        ) : (
                            <Play className="ml-0.5 h-4 w-4" />
                        )}
                    </button>
                    <button
                        data-testid="next-track-button"
                        onClick={onNext}
                        aria-label="Next track"
                        className="text-zinc-400 transition-all duration-300 hover:text-bone active:scale-90"
                    >
                        <SkipForward className="h-4 w-4" />
                    </button>
                </div>

                <div className="min-w-0 flex-1">
                    <p
                        data-testid="player-track-title"
                        className="truncate font-serif text-sm italic tracking-tight text-bone"
                    >
                        {track.title}
                    </p>
                    <div className="mt-1 flex items-center gap-3">
                        <span className="font-mono text-[10px] text-zinc-500">
                            {fmt(time)}
                        </span>
                        <div
                            data-testid="player-progress-bar"
                            role="slider"
                            aria-label="Seek"
                            aria-valuenow={Math.round(pct)}
                            onClick={(e) => {
                                const r =
                                    e.currentTarget.getBoundingClientRect();
                                onSeek((e.clientX - r.left) / r.width);
                            }}
                            className="group relative h-1 flex-1 cursor-pointer rounded bg-white/15"
                        >
                            <div
                                className="h-full rounded bg-blood transition-[width] duration-150"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <span className="font-mono text-[10px] text-zinc-500">
                            {fmt(duration)}
                        </span>
                    </div>
                </div>

                <div className="hidden items-center gap-2 md:flex">
                    <Volume2 className="h-4 w-4 text-zinc-500" />
                    <input
                        data-testid="volume-slider"
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={(e) => onVolume(Number(e.target.value))}
                        aria-label="Volume"
                        className="vol w-20"
                    />
                </div>
            </div>
        </div>
    );
}
